'use client'

import React, { useState } from 'react'

import { Box } from '@chakra-ui/react'

import { SortIndicator } from './SortIndicator'
import type { SortOrder } from './SortIndicator'
import {
  tableHeaderCellLabelStyles,
  tableHeaderCellStyles,
} from './Table.styles'
import {
  useTableColorScheme,
  useTableDensity,
  useTableSortContext,
} from './TableContext'

// ─── useCellSort ──────────────────────────────────────────────────────────────

const useCellSort = (
  column: string | undefined,
  sortOrder: SortOrder | undefined,
  onSort: (() => void) | undefined
) => {
  const ctx = useTableSortContext()
  const isContextManaged =
    column !== undefined && ctx.sortableColumns.includes(column)

  let order: SortOrder | undefined
  if (!isContextManaged) {
    order = sortOrder
  } else if (ctx.activeColumn === column) {
    order = ctx.activeOrder === 'asc' ? 'asc' : 'desc'
  } else {
    order = 'none'
  }

  const handleSort = isContextManaged ? () => ctx.onColumnSort(column!) : onSort
  const isSortable =
    isContextManaged || (sortOrder !== undefined && onSort !== undefined)
  const onKeyDown = isSortable
    ? (e: React.KeyboardEvent) => {
        if (
          e.target === e.currentTarget &&
          (e.key === 'Enter' || e.key === ' ')
        ) {
          e.preventDefault()
          handleSort?.()
        }
      }
    : undefined

  return { order, onSort: handleSort, onKeyDown, isSortable }
}

// ─── TableHeaderCell ──────────────────────────────────────────────────────────

export type TableHeaderCellProps = {
  children?: React.ReactNode
  /** Width of the column, e.g. '294px' or '48px'. */
  width?: string
  minWidth?: string
  maxWidth?: string
  /**
   * When set, the column grows proportionally with this weight (like CSS
   * flex-grow). Use instead of maxWidth for viewport-proportional columns.
   * flexBasis is set to '0px' so space is distributed purely by weight.
   */
  flexGrow?: number
  /**
   * Column key for context-managed sorting (pairs with Table's `sortableColumns`).
   * When provided and the column is in `sortableColumns`, sort state and click
   * handling are managed automatically — do not also pass `sortOrder`/`onSort`.
   */
  column?: string
  /**
   * Current sort direction for manually-managed sorting. Omit to render a
   * non-sortable column. Pass 'none' for unsorted (shows chevrons on hover).
   */
  sortOrder?: SortOrder
  /** Fired when the user clicks the header cell to change sort direction. */
  onSort?: () => void
  align?: 'left' | 'center' | 'right'
  /** Small numeral shown next to the sort chevrons (e.g. multi-key sort order). */
  sortBadge?: 1 | 2
}

export const TableHeaderCell = ({
  children,
  width,
  minWidth,
  maxWidth,
  flexGrow,
  column,
  sortOrder,
  onSort,
  align = 'left',
  sortBadge,
}: TableHeaderCellProps) => {
  const colorScheme = useTableColorScheme()
  const density = useTableDensity()
  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const {
    order,
    onSort: handleSort,
    onKeyDown,
    isSortable,
  } = useCellSort(column, sortOrder, onSort)

  return (
    <Box
      {...tableHeaderCellStyles(isSortable, density)}
      onBlur={() => setIsFocused(false)}
      onClick={
        isSortable
          ? (e: React.MouseEvent) => {
              const target = e.target as HTMLElement
              const interactiveAncestor = target.closest(
                'button, a, input, select, textarea, [role="button"], [role="link"], [data-prevent-sort]'
              )
              if (
                !interactiveAncestor ||
                interactiveAncestor === e.currentTarget
              )
                handleSort?.()
            }
          : undefined
      }
      onFocus={() => setIsFocused(true)}
      onKeyDown={onKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role={isSortable ? 'button' : undefined}
      style={{
        flexBasis: minWidth ?? width ?? '0px',
        flexGrow: flexGrow ?? 0,
        flexShrink: flexGrow !== undefined ? 0 : 1,
        minWidth: minWidth ?? width,
        maxWidth: maxWidth,
        justifyContent: align === 'center' ? 'center' : undefined,
      }}
      tabIndex={isSortable ? 0 : undefined}
    >
      {children && (
        <Box {...tableHeaderCellLabelStyles(colorScheme)}>{children}</Box>
      )}
      {order !== undefined && (
        <SortIndicator
          badge={sortBadge}
          isParentHovered={isHovered || isFocused}
          order={order}
        />
      )}
    </Box>
  )
}
